/**
 * PDF generation utility for the audit log.
 * Generates a comprehensive PDF containing all audit log information.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { SystemState, TreeValue } from "@unlikelyai-magic/utils";
import { calculateOutcomeChanges } from "../components";
import { guardrailPassed } from "../guardrailUtils";
import type { ConversationPair, InitialMessage } from "../types";

/** Colours used in the PDF */
const COLORS = {
  primary: [30, 64, 175] as [number, number, number],
  green: [22, 163, 74] as [number, number, number],
  red: [220, 38, 38] as [number, number, number],
  muted: [107, 114, 128] as [number, number, number],
  text: [17, 24, 39] as [number, number, number],
  lightGray: [243, 244, 246] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  blue: [37, 99, 235] as [number, number, number],
  yellow: [202, 138, 4] as [number, number, number],
};

const PAGE_MARGIN = 20;
const LINE_HEIGHT = 6;

/**
 * Strip markdown formatting from a string so it reads cleanly in PDF.
 */
function stripMarkdown(text: string): string {
  return (
    text
      // Remove images
      .replace(/!\[.*?\]\(.*?\)/g, "")
      // Convert links to just the text
      .replace(/\[([^\]]+)\]\(.*?\)/g, "$1")
      // Remove bold/italic markers
      .replace(/(\*{1,3}|_{1,3})(.*?)\1/g, "$2")
      // Remove inline code backticks
      .replace(/`([^`]+)`/g, "$1")
      // Remove code blocks
      .replace(/```[\s\S]*?```/g, "")
      // Remove headers
      .replace(/^#{1,6}\s+/gm, "")
      // Remove blockquotes
      .replace(/^>\s+/gm, "")
      // Clean up bullet list markers
      .replace(/^[\s]*[-*+]\s+/gm, "  - ")
      // Clean up numbered lists
      .replace(/^[\s]*\d+\.\s+/gm, "  ")
      // Collapse multiple newlines
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
}

/**
 * Format an Outcome enum value for human-readable display.
 */
function formatOutcome(outcome: Outcome | null | undefined): string {
  switch (outcome) {
    case Outcome.TRUE:
      return "Satisfied";
    case Outcome.FALSE:
      return "Not Eligible";
    case Outcome.UNDECIDED:
    case Outcome.UNKNOWN:
    case null:
      return "Undecided";
    default:
      return "Unknown";
  }
}

/**
 * Get a colour matching an outcome value.
 */
function outcomeColor(outcome: Outcome | null | undefined) {
  switch (outcome) {
    case Outcome.TRUE:
      return COLORS.green;
    case Outcome.FALSE:
      return COLORS.red;
    case Outcome.UNDECIDED:
    case Outcome.UNKNOWN:
    case null:
      return COLORS.yellow;
    default:
      return COLORS.muted;
  }
}

interface GenerateAuditLogPDFParams {
  sessionId?: string;
  isComplete?: boolean;
  conversations: ConversationPair[];
  initialMessages?: InitialMessage[];
  systemState: SystemState;
}

/**
 * Add a section title and return the new Y position.
 */
function addSectionTitle(
  doc: jsPDF,
  title: string,
  y: number,
  color = COLORS.primary
): number {
  const pageWidth = doc.internal.pageSize.getWidth();

  if (y > doc.internal.pageSize.getHeight() - 30) {
    doc.addPage();
    y = PAGE_MARGIN;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...color);
  doc.text(title, PAGE_MARGIN, y);
  y += 2;

  doc.setDrawColor(...color);
  doc.setLineWidth(0.5);
  doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y);
  y += LINE_HEIGHT;

  return y;
}

/**
 * Write wrapped body text and return the new Y position.
 */
function addWrappedText(
  doc: jsPDF,
  text: string,
  y: number,
  fontSize = 10,
  color = COLORS.text
): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - PAGE_MARGIN * 2;
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...color);

  const lines = doc.splitTextToSize(text, maxWidth) as string[];
  for (const line of lines) {
    if (y > pageHeight - 15) {
      doc.addPage();
      y = PAGE_MARGIN;
    }
    doc.text(line, PAGE_MARGIN, y);
    y += LINE_HEIGHT - 1;
  }
  y += 2;
  return y;
}

/**
 * Add a labelled value (e.g. "Status: Complete").
 */
function addLabelledValue(
  doc: jsPDF,
  label: string,
  value: string,
  y: number,
  valueColor = COLORS.text
): number {
  if (y > doc.internal.pageSize.getHeight() - 15) {
    doc.addPage();
    y = PAGE_MARGIN;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.muted);
  doc.text(`${label}: `, PAGE_MARGIN, y);

  const labelWidth = doc.getTextWidth(`${label}: `);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...valueColor);
  doc.text(value, PAGE_MARGIN + labelWidth, y);

  y += LINE_HEIGHT;
  return y;
}

/**
 * Render a tree value table (situations / segments / suggestions).
 */
function addTreeValueTable(
  doc: jsPDF,
  title: string,
  items: TreeValue[],
  y: number
): number {
  if (items.length === 0) return y;

  if (y > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage();
    y = PAGE_MARGIN;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  doc.text(title, PAGE_MARGIN, y);
  y += LINE_HEIGHT;

  autoTable(doc, {
    startY: y,
    head: [["Name", "Status"]],
    body: items.map((item) => [item.name, formatOutcome(item.value)]),
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontSize: 9,
      fontStyle: "bold",
    },
    bodyStyles: { fontSize: 9, textColor: COLORS.text },
    alternateRowStyles: { fillColor: COLORS.lightGray },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    didParseCell(data: any) {
      if (data.section === "body" && data.column.index === 1) {
        const val = items[data.row.index]?.value;
        data.cell.styles.textColor = outcomeColor(val);
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + LINE_HEIGHT;
  return y;
}

/**
 * Generates a PDF of the full audit log and triggers a download.
 */
export function generateAuditLogPDF({
  sessionId,
  isComplete,
  conversations,
  initialMessages = [],
  systemState,
}: GenerateAuditLogPDFParams) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = PAGE_MARGIN;

  // ── Title ────────────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.primary);
  doc.text("Audit Log Report", pageWidth / 2, y, { align: "center" });
  y += 10;

  // ── Session info ─────────────────────────────────────────
  if (sessionId) {
    y = addLabelledValue(doc, "Session ID", sessionId, y);
  }
  y = addLabelledValue(
    doc,
    "Status",
    isComplete ? "Complete" : "In Progress",
    y,
    isComplete ? COLORS.green : COLORS.muted
  );
  y = addLabelledValue(
    doc,
    "Generated",
    new Date().toLocaleString(),
    y,
    COLORS.muted
  );
  y += 4;

  // ── Summary statistics ───────────────────────────────────
  y = addSectionTitle(doc, "Summary", y);

  const suggestions = Array.from(systemState.suggestions.values());
  const totalTurns = conversations.length;
  const totalNodesChanged = conversations.reduce(
    (sum, c) => sum + c.changedNodes.length,
    0
  );
  const satisfiedCount = suggestions.filter(
    (s) => s.value === Outcome.TRUE
  ).length;
  const discardedCount = suggestions.filter(
    (s) => s.value === Outcome.FALSE
  ).length;

  autoTable(doc, {
    startY: y,
    head: [["Total Turns", "Nodes Changed", "Satisfied", "Discarded"]],
    body: [
      [
        String(totalTurns),
        String(totalNodesChanged),
        String(satisfiedCount),
        String(discardedCount),
      ],
    ],
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontSize: 9,
      fontStyle: "bold",
    },
    bodyStyles: { fontSize: 11, fontStyle: "bold", halign: "center" },
    columnStyles: {
      2: { textColor: COLORS.green },
      3: { textColor: COLORS.red },
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + LINE_HEIGHT + 2;

  // ── Final System State ───────────────────────────────────
  y = addSectionTitle(doc, "Final System State", y);

  const situations = Array.from(systemState.situations.values());
  const segments = Array.from(systemState.segments.values());

  y = addTreeValueTable(doc, "Situations", situations, y);
  y = addTreeValueTable(doc, "Segments", segments, y);
  y = addTreeValueTable(doc, "Suggestions", suggestions, y);
  y += 2;

  // ── Initial Messages ─────────────────────────────────────
  if (initialMessages.length > 0) {
    y = addSectionTitle(doc, "Initial Messages", y);

    for (const msg of initialMessages) {
      y = addWrappedText(doc, stripMarkdown(msg.content), y);
      y += 2;
    }
  }

  // ── Conversation Turns ───────────────────────────────────
  if (conversations.length > 0) {
    y = addSectionTitle(doc, `Conversation Turns (${conversations.length})`, y);
  }

  conversations.forEach((conversation, idx) => {
    const previousConversation = idx > 0 ? conversations[idx - 1] : undefined;

    // Turn header
    if (y > doc.internal.pageSize.getHeight() - 30) {
      doc.addPage();
      y = PAGE_MARGIN;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.primary);
    doc.text(`Turn ${idx + 1}`, PAGE_MARGIN, y);
    y += LINE_HEIGHT;

    // ── Assistant Response ────────────────────────────────
    if (conversation.systemResponse) {
      const hasOutputGuardrails =
        conversation.outputGuardrailTrees &&
        conversation.outputGuardrailTrees.length > 0;

      // Guardrail rephrase history
      if (hasOutputGuardrails && conversation.outputGuardrailTrees.length > 1) {
        for (
          let gIdx = 0;
          gIdx < conversation.outputGuardrailTrees.length - 1;
          gIdx++
        ) {
          const gt = conversation.outputGuardrailTrees[gIdx];
          const iterationFailed = gt.guardrail_trees?.some(
            (gr) => !guardrailPassed(gr)
          );

          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(...COLORS.yellow);
          const attemptLabel = `Attempt ${gt.sequence_number} - ${
            iterationFailed ? "Guardrails triggered" : "Rephrased"
          }`;

          if (y > doc.internal.pageSize.getHeight() - 20) {
            doc.addPage();
            y = PAGE_MARGIN;
          }
          doc.text(attemptLabel, PAGE_MARGIN + 4, y);
          y += LINE_HEIGHT - 1;

          if (gt.message?.content) {
            y = addWrappedText(
              doc,
              stripMarkdown(gt.message.content),
              y,
              9,
              COLORS.muted
            );
          }

          // Failed guardrail names
          const failedGuardrails =
            gt.guardrail_trees?.filter((gr) => !guardrailPassed(gr)) ?? [];
          if (failedGuardrails.length > 0) {
            doc.setFont("helvetica", "italic");
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.yellow);
            const names = failedGuardrails
              .map((gr) => gr.guardrail_tree.name)
              .join(", ");
            doc.text(`Triggered: ${names}`, PAGE_MARGIN + 4, y);
            y += LINE_HEIGHT;
          }
        }
      }

      // Final assistant response
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.muted);

      if (y > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        y = PAGE_MARGIN;
      }

      const responseLabel = hasOutputGuardrails
        ? "Assistant Response (passed output guardrails):"
        : "Assistant Response:";
      doc.text(responseLabel, PAGE_MARGIN, y);
      y += LINE_HEIGHT - 1;

      y = addWrappedText(doc, stripMarkdown(conversation.systemResponse), y);
    }

    // ── User Message ─────────────────────────────────────
    if (conversation.userMessage) {
      // Input guardrails
      const orderedIterations =
        conversation.inputGuardrailTrees
          ?.slice()
          .sort((a, b) => a.sequence_number - b.sequence_number) ?? [];
      const lastGuardrailIteration = orderedIterations.at(-1);
      const failedInputGuardrails =
        lastGuardrailIteration?.guardrail_trees?.filter(
          (tree) => !guardrailPassed(tree)
        ) ?? [];
      const hasInputGuardrails = orderedIterations.length > 0;
      const inputGuardrailsFailed = failedInputGuardrails.length > 0;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.muted);

      if (y > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        y = PAGE_MARGIN;
      }

      let userLabel = "User Message:";
      if (hasInputGuardrails) {
        userLabel = inputGuardrailsFailed
          ? "User Message (input guardrails failed):"
          : "User Message (passed input guardrails):";
      }
      doc.text(userLabel, PAGE_MARGIN, y);
      y += LINE_HEIGHT - 1;

      y = addWrappedText(doc, stripMarkdown(conversation.userMessage), y);

      if (inputGuardrailsFailed) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.red);
        const names = failedInputGuardrails
          .map((gr) => gr.guardrail_tree.name)
          .join(", ");
        doc.text(`Failed guardrails: ${names}`, PAGE_MARGIN + 4, y);
        y += LINE_HEIGHT;
      }
    }

    // ── Target Questions ─────────────────────────────────
    if (
      conversation.targetQuestions &&
      conversation.targetQuestions.length > 0
    ) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.muted);

      if (y > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        y = PAGE_MARGIN;
      }

      doc.text("Target Questions:", PAGE_MARGIN, y);
      y += LINE_HEIGHT - 1;

      for (const q of conversation.targetQuestions) {
        y = addWrappedText(doc, `- ${q}`, y, 9);
      }
    }

    // ── Changed Nodes ────────────────────────────────────
    if (conversation.changedNodes.length > 0) {
      if (y > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        y = PAGE_MARGIN;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.muted);
      doc.text(
        `Changed Nodes (${conversation.changedNodes.length}):`,
        PAGE_MARGIN,
        y
      );
      y += LINE_HEIGHT - 1;

      autoTable(doc, {
        startY: y,
        head: [["Question", "Value", "Reasoning"]],
        body: conversation.changedNodes.map((node) => [
          node.question,
          node.value === Outcome.TRUE ? "TRUE" : "FALSE",
          node.reasoning ?? "-",
        ]),
        margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
        headStyles: {
          fillColor: COLORS.primary,
          textColor: COLORS.white,
          fontSize: 8,
          fontStyle: "bold",
        },
        bodyStyles: { fontSize: 8, textColor: COLORS.text },
        alternateRowStyles: { fillColor: COLORS.lightGray },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 15, halign: "center", fontStyle: "bold" },
          2: { cellWidth: "auto" },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        didParseCell(data: any) {
          if (data.section === "body" && data.column.index === 1) {
            const val = conversation.changedNodes[data.row.index]?.value;
            data.cell.styles.textColor =
              val === Outcome.TRUE ? COLORS.green : COLORS.red;
          }
        },
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      y = (doc as any).lastAutoTable.finalY + LINE_HEIGHT;
    }

    // ── Outcome Changes ──────────────────────────────────
    const outcomeChanges = calculateOutcomeChanges(
      conversation.outcomeStates,
      previousConversation?.outcomeStates
    );

    if (outcomeChanges.length > 0) {
      if (y > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        y = PAGE_MARGIN;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.blue);
      doc.text("Suggestions Changed:", PAGE_MARGIN, y);
      y += LINE_HEIGHT - 1;

      autoTable(doc, {
        startY: y,
        head: [["Name", "Type", "Previous", "New"]],
        body: outcomeChanges.map((change) => [
          change.name,
          change.type.charAt(0).toUpperCase() + change.type.slice(1),
          formatOutcome(change.previousOutcome),
          formatOutcome(change.newOutcome),
        ]),
        margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
        headStyles: {
          fillColor: COLORS.blue,
          textColor: COLORS.white,
          fontSize: 8,
          fontStyle: "bold",
        },
        bodyStyles: { fontSize: 8, textColor: COLORS.text },
        alternateRowStyles: { fillColor: COLORS.lightGray },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        didParseCell(data: any) {
          if (data.section === "body" && data.column.index === 3) {
            const change = outcomeChanges[data.row.index];
            data.cell.styles.textColor = outcomeColor(change?.newOutcome);
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      y = (doc as any).lastAutoTable.finalY + LINE_HEIGHT;
    }

    // Turn separator
    y += 4;
    if (y < doc.internal.pageSize.getHeight() - 10) {
      doc.setDrawColor(...COLORS.lightGray);
      doc.setLineWidth(0.3);
      doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y);
      y += LINE_HEIGHT;
    }
  });

  // ── Footer on every page ─────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, {
      align: "center",
    });
  }

  // ── Download ─────────────────────────────────────────────
  const filename = sessionId
    ? `audit-log-${sessionId.slice(0, 8)}.pdf`
    : `audit-log-${Date.now()}.pdf`;
  doc.save(filename);
}
